import { MapPin, Phone, Globe, GraduationCap, Calendar, Hash, Zap, Users } from 'lucide-react';
import { DirectionsButton } from '../directions';
import { hasDirectionsDestination } from '../../lib/directions';
import { formatPhone } from '../../lib/formatPhone';
import type { OrganizationWithPeople } from '../../types';

interface OrganizationInfoProps {
  organization: OrganizationWithPeople;
}

/**
 * NCES Information card (read-only)
 * Displays school data from NCES database
 */
export const OrganizationInfo = ({ organization }: OrganizationInfoProps) => {
  // CRM-71: the NCES "School Information" card is school-specific data. Clubs/bands
  // have no NCES record — render nothing rather than fabricated school fields.
  if (organization.type !== 'SCHOOL') return null;

  // Format grade range
  const gradeRange =
    organization.ncesGradeLow && organization.ncesGradeHigh
      ? `${organization.ncesGradeLow} - ${organization.ncesGradeHigh}`
      : null;

  // Enrollment (NCES "size"). Truthy guard hides null AND 0 — a 0 means
  // membership wasn't finalized, not a real size (see CRM-34).
  const enrollment = organization.ncesEnrollment
    ? organization.ncesEnrollment.toLocaleString()
    : null;

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        School Information
      </h2>
      {organization.blastSchoolId ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 flex items-center">
          <Zap className="h-3 w-3 mr-1 text-green-600 dark:text-green-400" />
          Source: Blast — Read only
        </p>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          Data from NCES ({organization.ncesDataYear || 'Unknown year'}) — Read only
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column */}
        <div className="space-y-4">
          {/* Address */}
          <InfoItem
            icon={<MapPin className="h-5 w-5" />}
            label="Address"
            value={
              <div>
                {organization.address && <div>{organization.address}</div>}
                <div>
                  {[organization.city, organization.state, organization.zip]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                {hasDirectionsDestination(organization) && (
                  <div className="mt-1.5">
                    <DirectionsButton destination={organization} variant="inline" />
                  </div>
                )}
              </div>
            }
          />

          {/* Phone */}
          <InfoItem
            icon={<Phone className="h-5 w-5" />}
            label="Phone"
            value={formatPhone(organization.phone)}
          />

          {/* Website */}
          {organization.website && (
            <InfoItem
              icon={<Globe className="h-5 w-5" />}
              label="Website"
              value={
                <a
                  href={
                    organization.website.startsWith('http')
                      ? organization.website
                      : `https://${organization.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-400 hover:underline"
                >
                  {organization.website}
                </a>
              }
            />
          )}

          {/* District */}
          <InfoItem
            icon={<Hash className="h-5 w-5" />}
            label="District"
            value={organization.district}
          />
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* School Type */}
          <InfoItem
            icon={<GraduationCap className="h-5 w-5" />}
            label="School Type"
            value={organization.ncesSchoolType}
          />

          {/* Grade Range */}
          <InfoItem
            icon={<GraduationCap className="h-5 w-5" />}
            label="Grade Range"
            value={gradeRange}
          />

          {/* Level */}
          <InfoItem
            icon={<GraduationCap className="h-5 w-5" />}
            label="Level"
            value={organization.ncesLevel}
          />

          {/* Enrollment */}
          <InfoItem
            icon={<Users className="h-5 w-5" />}
            label="Enrollment"
            value={enrollment}
          />

          {/* NCES ID */}
          <InfoItem
            icon={<Hash className="h-5 w-5" />}
            label="NCES ID"
            value={
              <span className="font-mono text-sm">{organization.ncesId}</span>
            }
          />

          {/* Data Year */}
          <InfoItem
            icon={<Calendar className="h-5 w-5" />}
            label="Data Year"
            value={organization.ncesDataYear}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Single info item with icon
 */
interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const InfoItem = ({ icon, label, value }: InfoItemProps) => {
  if (!value) {
    return null;
  }

  return (
    <div className="flex items-start space-x-3">
      <div className="text-gray-400 dark:text-gray-500 mt-0.5">{icon}</div>
      <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
        <dd className="text-sm text-gray-900 dark:text-gray-100">{value}</dd>
      </div>
    </div>
  );
};
